import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  BarChart3,
  LineChart as LineChartIcon,
  Network,
  Pause,
  PieChart as PieChartIcon,
  Play,
  RotateCcw,
  ScatterChart as ScatterChartIcon,
  X,
} from "lucide-react";
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart as RechartsLineChart,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
  Scatter,
  ScatterChart as RechartsScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DEFAULT_DATA_VIZ_NODE_COLORS,
  type DataVizCameraDiagnostics,
  type DataVizChart,
  type DataVizRendererController,
  type PartialDataVizTheme,
} from "@triedge/data-viz-three";
import {
  mountDataVizThreeChartScene,
  mountDataVizThreeScene,
} from "@triedge/data-viz-three/three";
import {
  isReportGenerationThreeChartType,
  projectReportGenerationAdvancedVisualizationChart,
} from "./advancedVisualizationChart";
import { projectReportGenerationAdvancedVisualizationGraph } from "./advancedVisualizationGraph";
import type {
  ReportGenerationAdvancedVisualizationActiveView,
  ReportGenerationAdvancedVisualizationChartAnimationVariant,
  ReportGenerationAdvancedVisualizationChartOption,
  ReportGenerationAdvancedVisualizationChartPoint,
  ReportGenerationAdvancedVisualizationChartType,
  ReportGenerationAdvancedVisualizationSnapshot,
  ReportGenerationSurfaceTheme,
} from "./types";

export type ReportGenerationAdvancedVisualizationProps = {
  snapshot: ReportGenerationAdvancedVisualizationSnapshot;
  onClose: () => void;
  initialView?: ReportGenerationAdvancedVisualizationActiveView;
  activeView?: ReportGenerationAdvancedVisualizationActiveView;
  onActiveViewChange?: (
    view: ReportGenerationAdvancedVisualizationActiveView,
  ) => void;
  initialChartType?: ReportGenerationAdvancedVisualizationChartType;
  activeChartType?: ReportGenerationAdvancedVisualizationChartType | null;
  onActiveChartTypeChange?: (
    chartType: ReportGenerationAdvancedVisualizationChartType,
  ) => void;
  surfaceTheme?: ReportGenerationSurfaceTheme;
  presentation?: ReportGenerationAdvancedVisualizationPresentation;
};

type ReportGenerationAdvancedVisualizationPresentation = "inline" | "workspace";
type RendererState = "loading" | "ready" | "unavailable" | "error";
type ActiveView = ReportGenerationAdvancedVisualizationActiveView;
const GRAPH_DISABLED_FALLBACK_VIEW: ActiveView = "chart";
type GraphSceneSize = "compact" | "standard" | "expanded";
type ChartSceneKind = "graph" | "chart";
type SurfaceThemePalette = {
  containerBgClassName: string;
  containerBorderClassName: string;
  containerShadowClassName: string;
  headerBgClassName: string;
  headerTextPrimaryClassName: string;
  headerTextSecondaryClassName: string;
  controlBgClassName: string;
  controlBorderClassName: string;
  controlActiveBgClassName: string;
  controlActiveTextClassName: string;
  controlInactiveTextClassName: string;
  controlInactiveHoverTextClassName: string;
  closeButtonBgClassName: string;
  closeButtonBorderClassName: string;
  closeButtonTextClassName: string;
  closeButtonHoverBgClassName: string;
  stageBgClassName: string;
  stagePanelBgClassName: string;
  sideRailBgClassName: string;
  sideRailBorderClassName: string;
  railCardBorderClassName: string;
  railCardTextClassName: string;
  accentTextClassName: string;
  accentChipBgClassName: string;
  accentChipBorderClassName: string;
  accentChipTextClassName: string;
  summaryTextClassName: string;
  summaryValueTextClassName: string;
  warningTextClassName: string;
  chartPanelBgClassName: string;
  chartPanelBorderClassName: string;
  chartSelectorBgClassName: string;
  chartSelectorBorderClassName: string;
  chartSelectorActiveBgClassName: string;
  chartSelectorActiveTextClassName: string;
  chartSelectorInactiveTextClassName: string;
  chartSelectorInactiveHoverBgClassName: string;
  chartSelectorInactiveHoverTextClassName: string;
  emptyStateTextClassName: string;
  graphBackground: number;
  graphEdgeColor: number;
  chartGridColor: string;
  chartAxisColor: string;
  chartLegendColor: string;
  chartSeriesColor: string;
  chartScatterCursorColor: string;
};
type ChartLegendItem = {
  key: string;
  label: string;
  color: string;
  value?: string | null;
};

const GRAPH_SCENE_SIZE_PRESETS: Record<GraphSceneSize, number> = {
  compact: 420,
  standard: 560,
  expanded: 720,
};
const GRAPH_SCENE_MIN_HEIGHT = 320;
const GRAPH_SCENE_VIEWPORT_GUTTER = 24;
const GRAPH_DESKTOP_BREAKPOINT = 1024;
const GRAPH_SCENE_SIZE_LABELS: Record<GraphSceneSize, string> = {
  compact: "Compact",
  standard: "Standard",
  expanded: "Expanded",
};
const DATASOURCE_FAMILY_LABEL_OVERRIDES: Record<string, string> = {
  "studentsFamilies.records": "Student and family records",
  "studentsFamilies.tuition": "Tuition records",
  "admissions.reporting": "Admissions reporting data",
  "financialAid.reviews": "Financial aid review data",
};

const NODE_COLORS = DEFAULT_DATA_VIZ_NODE_COLORS;

const CHART_COLORS = [
  "#2697A2",
  "#237062",
  "#08BFAE",
  "#C57A00",
  "#7C3AED",
  "#CC4444",
  "#496A65",
  "#404F4D",
  "#94A3B8",
];

const CHART_TYPE_LABELS: Record<
  ReportGenerationAdvancedVisualizationChartType,
  string
> = {
  bar: "Bar",
  line: "Line",
  pie: "Pie",
  scatter: "Scatter",
};

const CHART_TYPE_ICONS = {
  bar: BarChart3,
  line: LineChartIcon,
  pie: PieChartIcon,
  scatter: ScatterChartIcon,
} as const;

const joinClasses = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(" ");

const formatHexColor = (value: number): string =>
  `#${value.toString(16).padStart(6, "0")}`;

const buildReportGenerationDataVizTheme = (
  surfaceTheme: ReportGenerationSurfaceTheme,
  palette: SurfaceThemePalette,
  sceneKind: ChartSceneKind,
): PartialDataVizTheme => {
  const isDarkTheme = surfaceTheme === "dark";
  const theme: PartialDataVizTheme = {
    background: formatHexColor(palette.graphBackground),
    edgeColor: sceneKind === "chart"
      ? palette.chartGridColor
      : formatHexColor(palette.graphEdgeColor),
    labelTextColor: isDarkTheme ? "#E8EFEF" : "#233236",
    labelBackgroundColor: isDarkTheme
      ? "rgba(6, 11, 12, 0.86)"
      : "rgba(255, 255, 255, 0.9)",
    labelBorderColor: isDarkTheme
      ? "rgba(143, 183, 174, 0.48)"
      : "rgba(35, 112, 98, 0.24)",
  };

  if (sceneKind === "chart") {
    return {
      ...theme,
      camera: {
        position: [0, 82, 268],
        fov: 42,
      },
      lighting: {
        ambientIntensity: isDarkTheme ? 2.25 : 2,
        directionalIntensity: isDarkTheme ? 1.65 : 1.5,
        directionalPosition: [120, 170, 260],
      },
    };
  }

  return {
    ...theme,
    labelBackgroundColor: isDarkTheme
      ? "rgba(6, 11, 12, 0.82)"
      : "rgba(255, 255, 255, 0.88)",
    labelBorderColor: isDarkTheme
      ? "rgba(143, 183, 174, 0.5)"
      : "rgba(35, 112, 98, 0.28)",
  };
};

const uniqueSorted = (values: readonly string[]): string[] =>
  [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort((left, right) =>
    left.localeCompare(right),
  );

const titleCase = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      if (/^(id|api)$/i.test(word)) {
        return word.toUpperCase();
      }
      return `${word.slice(0, 1).toUpperCase()}${word.slice(1).toLowerCase()}`;
    })
    .join(" ");

const humanizeToken = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const humanizeDatasourceFamily = (family: string): string =>
  DATASOURCE_FAMILY_LABEL_OVERRIDES[family]
  ?? titleCase(humanizeToken(family));

const formatPersonaLabel = (persona: string): string =>
  titleCase(persona.replace(/-/g, " "));

const formatJoinNarrative = (label: string): string =>
  label
    .replace(/\s*↔\s*/g, " and ")
    .replace(/\s+linked by\s+/i, " are matched by ")
    .trim();

const REPORT_GENERATION_SURFACE_THEME_PALETTES: Record<
  ReportGenerationSurfaceTheme,
  SurfaceThemePalette
> = {
  light: {
    containerBgClassName: "bg-white",
    containerBorderClassName: "border-[#CDEEE9]",
    containerShadowClassName: "shadow-[0_14px_32px_rgba(15,23,42,0.06)]",
    headerBgClassName: "bg-white",
    headerTextPrimaryClassName: "text-[#404F4D]",
    headerTextSecondaryClassName: "text-[#6B7280]",
    controlBgClassName: "bg-[#F8FAF9]",
    controlBorderClassName: "border-[#E5E7EB]",
    controlActiveBgClassName: "bg-white",
    controlActiveTextClassName: "text-[#237062]",
    controlInactiveTextClassName: "text-[#6B7280]",
    controlInactiveHoverTextClassName: "hover:text-[#404F4D]",
    closeButtonBgClassName: "bg-white",
    closeButtonBorderClassName: "border-[#E5E7EB]",
    closeButtonTextClassName: "text-[#6B7280]",
    closeButtonHoverBgClassName: "hover:bg-[#F8FAF9] hover:text-[#404F4D]",
    stageBgClassName: "bg-[#F8FAF9]",
    stagePanelBgClassName: "bg-[#F8FAF9]",
    sideRailBgClassName: "bg-white",
    sideRailBorderClassName: "border-[#E5E7EB]",
    railCardBorderClassName: "border-[#E5E7EB]",
    railCardTextClassName: "text-[#404F4D]",
    accentTextClassName: "text-[#2697A2]",
    accentChipBgClassName: "bg-[#E8F7F6]",
    accentChipBorderClassName: "border-[#CDEEE9]",
    accentChipTextClassName: "text-[#237062]",
    summaryTextClassName: "text-[#6B7280]",
    summaryValueTextClassName: "text-[#404F4D]",
    warningTextClassName: "text-[#6B7280]",
    chartPanelBgClassName: "bg-white",
    chartPanelBorderClassName: "border-[#E5E7EB]",
    chartSelectorBgClassName: "bg-white",
    chartSelectorBorderClassName: "border-[#D8E6E3]",
    chartSelectorActiveBgClassName: "bg-[#2697A2]",
    chartSelectorActiveTextClassName: "text-white",
    chartSelectorInactiveTextClassName: "text-[#6B7280]",
    chartSelectorInactiveHoverBgClassName: "hover:bg-[#F4F6F3]",
    chartSelectorInactiveHoverTextClassName: "hover:text-[#404F4D]",
    emptyStateTextClassName: "text-[#6B7280]",
    graphBackground: 0xf8faf9,
    graphEdgeColor: 0x9ca3af,
    chartGridColor: "#E5E7EB",
    chartAxisColor: "#6B7280",
    chartLegendColor: "#404F4D",
    chartSeriesColor: "#2697A2",
    chartScatterCursorColor: "#94A3B8",
  },
  dark: {
    containerBgClassName: "bg-[#060B0C]",
    containerBorderClassName: "border-[#1B262B]",
    containerShadowClassName: "shadow-[0_22px_46px_rgba(0,0,0,0.34)]",
    headerBgClassName: "bg-[#060B0C]",
    headerTextPrimaryClassName: "text-[#E8EFEF]",
    headerTextSecondaryClassName: "text-[#91A2A6]",
    controlBgClassName: "bg-[#0B1214]",
    controlBorderClassName: "border-[#223137]",
    controlActiveBgClassName: "bg-[#060B0C]",
    controlActiveTextClassName: "text-[#8FB7AE]",
    controlInactiveTextClassName: "text-[#91A2A6]",
    controlInactiveHoverTextClassName: "hover:text-[#D7E1E2]",
    closeButtonBgClassName: "bg-[#060B0C]",
    closeButtonBorderClassName: "border-[#223137]",
    closeButtonTextClassName: "text-[#91A2A6]",
    closeButtonHoverBgClassName: "hover:bg-[#0D1517] hover:text-[#E8EFEF]",
    stageBgClassName: "bg-[#071112]",
    stagePanelBgClassName: "bg-[#071112]",
    sideRailBgClassName: "bg-[#060B0C]",
    sideRailBorderClassName: "border-[#1B262B]",
    railCardBorderClassName: "border-[#223137]",
    railCardTextClassName: "text-[#E8EFEF]",
    accentTextClassName: "text-[#8FB7AE]",
    accentChipBgClassName: "bg-[#0C2528]",
    accentChipBorderClassName: "border-[#1B4A4E]",
    accentChipTextClassName: "text-[#8FB7AE]",
    summaryTextClassName: "text-[#91A2A6]",
    summaryValueTextClassName: "text-[#E8EFEF]",
    warningTextClassName: "text-[#91A2A6]",
    chartPanelBgClassName: "bg-[#071112]",
    chartPanelBorderClassName: "border-[#223137]",
    chartSelectorBgClassName: "bg-[#0B1214]",
    chartSelectorBorderClassName: "border-[#223137]",
    chartSelectorActiveBgClassName: "bg-[#0C2528]",
    chartSelectorActiveTextClassName: "text-[#8FB7AE]",
    chartSelectorInactiveTextClassName: "text-[#91A2A6]",
    chartSelectorInactiveHoverBgClassName: "hover:bg-[#101B1E]",
    chartSelectorInactiveHoverTextClassName: "hover:text-[#E8EFEF]",
    emptyStateTextClassName: "text-[#91A2A6]",
    graphBackground: 0x071112,
    graphEdgeColor: 0x32464c,
    chartGridColor: "#2B3D42",
    chartAxisColor: "#91A2A6",
    chartLegendColor: "#E8EFEF",
    chartSeriesColor: "#2697A2",
    chartScatterCursorColor: "#90A0A5",
  },
};

const CHART_ANIMATION_VARIANTS: Record<
  ReportGenerationAdvancedVisualizationChartAnimationVariant,
  { begin: number; duration: number; easing: "ease" | "ease-in" | "ease-out" | "ease-in-out" | "linear" }
> = {
  default: { begin: 0, duration: 800, easing: "ease-out" },
  quick: { begin: 0, duration: 420, easing: "ease-out" },
  staggered: { begin: 180, duration: 1000, easing: "ease-in-out" },
  smooth: { begin: 80, duration: 1200, easing: "ease" },
};

const formatChartValue = (value: unknown): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "-";
  }
  return Math.abs(value) >= 1000
    ? value.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

type RechartsCartesianPoint = {
  key: string;
  label: string;
  [seriesKey: string]: string | number | null;
};

type RechartsSeriesDefinition = {
  id: string;
  key: string;
  label: string;
  color: string;
};

const normalizeRechartsKey = (value: string | null | undefined): string =>
  `series_${String(value ?? "value")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "value"}`;

const truncateChartLabel = (value: unknown, maxLength = 18): string => {
  const text = String(value ?? "").trim();
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, Math.max(0, maxLength - 1))}...`;
};

const finiteChartPointValue = (
  point: ReportGenerationAdvancedVisualizationChartPoint,
): number | null => {
  if (typeof point.value === "number" && Number.isFinite(point.value)) {
    return point.value;
  }
  if (typeof point.sum === "number" && Number.isFinite(point.sum)) {
    return point.sum;
  }
  if (typeof point.average === "number" && Number.isFinite(point.average)) {
    return point.average;
  }
  if (typeof point.count === "number" && Number.isFinite(point.count)) {
    return point.count;
  }
  return null;
};

const buildRechartsCartesianDataset = (
  option: ReportGenerationAdvancedVisualizationChartOption,
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
): {
  data: RechartsCartesianPoint[];
  series: RechartsSeriesDefinition[];
} => {
  const rowsByKey = new Map<string, RechartsCartesianPoint>();
  const seriesById = new Map<string, RechartsSeriesDefinition>();
  for (const point of points) {
    const value = finiteChartPointValue(point);
    if (value === null) {
      continue;
    }
    const seriesId = point.seriesId ?? option.metricFieldId ?? option.id;
    const seriesKey = normalizeRechartsKey(seriesId);
    if (!seriesById.has(seriesId)) {
      seriesById.set(seriesId, {
        id: seriesId,
        key: seriesKey,
        label: point.seriesLabel ?? option.metricLabel ?? option.label,
        color:
          point.seriesColor ??
          CHART_COLORS[seriesById.size % CHART_COLORS.length] ??
          CHART_COLORS[0],
      });
    }
    const rowKey = point.key || point.bucket || point.label;
    const existing = rowsByKey.get(rowKey) ?? {
      key: rowKey,
      label: point.label || point.bucket || rowKey,
    };
    existing[seriesKey] = value;
    rowsByKey.set(rowKey, existing);
  }
  return {
    data: [...rowsByKey.values()],
    series: [...seriesById.values()],
  };
};

const chartAxisLabels = (
  option: ReportGenerationAdvancedVisualizationChartOption | null,
): {
  x: string;
  y: string;
  series: string | null;
} => ({
  x: option?.dimensionLabel ?? option?.xLabel ?? "Bucket",
  y: option?.metricLabel ?? option?.yLabel ?? "Value",
  series: option?.groupLabel ?? null,
});

const usePrefersReducedMotion = (): boolean => {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return undefined;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(query.matches);
    const listener = (event: MediaQueryListEvent) =>
      setReducedMotion(event.matches);
    query.addEventListener?.("change", listener);
    return () => query.removeEventListener?.("change", listener);
  }, []);

  return reducedMotion;
};

const chartOptionLabel = (
  option: ReportGenerationAdvancedVisualizationChartOption | null,
): string => {
  if (!option) {
    return "Aggregate chart";
  }
  if (option.chartType === "scatter") {
    return `${option.xLabel ?? "X"} vs ${option.yLabel ?? "Y"}`;
  }
  return option.metricLabel ?? option.dimensionLabel ?? "Rows";
};

const tooltipFormatter = (value: unknown, name: unknown) => [
  formatChartValue(value),
  String(name ?? "Value"),
];

const tooltipLabelFormatter = (label: unknown) => String(label ?? "Aggregate");

const resolveChartAnimationVariant = (
  option: ReportGenerationAdvancedVisualizationChartOption,
): ReportGenerationAdvancedVisualizationChartAnimationVariant => {
  const variant = option.animationVariant;
  return variant && variant in CHART_ANIMATION_VARIANTS ? variant : "default";
};

const buildChartAnimationProps = (
  option: ReportGenerationAdvancedVisualizationChartOption,
  reducedMotion: boolean,
) => {
  const animation = CHART_ANIMATION_VARIANTS[resolveChartAnimationVariant(option)];
  return {
    isAnimationActive: !reducedMotion,
    animationBegin: reducedMotion ? 0 : animation.begin,
    animationDuration: reducedMotion ? 0 : animation.duration,
    animationEasing: animation.easing,
  };
};

const renderExternalChartLegend = (
  items: readonly ChartLegendItem[],
  palette: SurfaceThemePalette,
) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <div
      className={joinClasses(
        "mt-3 flex max-h-[82px] shrink-0 flex-wrap items-start gap-x-4 gap-y-2 overflow-y-auto border-t pt-3 text-[11px] font-semibold leading-4",
        palette.chartPanelBorderClassName,
      )}
      style={{ color: palette.chartLegendColor }}
      data-testid="report-generation-advanced-visualization-chart-legend"
      role="list"
      aria-label="Chart legend"
    >
      {items.map((item) => (
        <span
          key={item.key}
          className="inline-flex min-w-0 max-w-[240px] items-start gap-1.5"
          role="listitem"
          title={item.value ? `${item.label}: ${item.value}` : item.label}
        >
          <span
            className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: item.color }}
            aria-hidden="true"
          />
          <span className="min-w-0 break-words">
            {item.label}
            {item.value ? (
              <span className="font-medium opacity-75"> · {item.value}</span>
            ) : null}
          </span>
        </span>
      ))}
    </div>
  );
};

const renderRechartsStageFrame = ({
  children,
  className,
  style,
  legendItems,
  palette,
}: {
  children: ReactNode;
  className: string;
  style?: CSSProperties;
  legendItems: readonly ChartLegendItem[];
  palette: SurfaceThemePalette;
}) => (
  <div
    className={joinClasses(className, "flex flex-col overflow-hidden")}
    style={style}
    data-testid="report-generation-advanced-visualization-chart-recharts-stage"
    data-chart-renderer="recharts"
  >
    <div
      className="min-h-0 flex-1"
      data-testid="report-generation-advanced-visualization-chart-plot"
    >
      {children}
    </div>
    {renderExternalChartLegend(legendItems, palette)}
  </div>
);

const renderRechartsFallbackChart = (
  option: ReportGenerationAdvancedVisualizationChartOption,
  points: readonly ReportGenerationAdvancedVisualizationChartPoint[],
  reducedMotion: boolean,
  palette: SurfaceThemePalette,
  presentation: ReportGenerationAdvancedVisualizationPresentation,
) => {
  const chartPoints = [...points];
  const animationProps = buildChartAnimationProps(option, reducedMotion);
  const isWorkspacePresentation = presentation === "workspace";
  const aggregateStageStyle: CSSProperties | undefined = isWorkspacePresentation
    ? { height: "clamp(460px, 48vh, 660px)" }
    : undefined;
  const cartesianStageStyle: CSSProperties | undefined = isWorkspacePresentation
    ? { height: "clamp(500px, 52vh, 720px)" }
    : undefined;
  const aggregateStageClassName = isWorkspacePresentation
    ? "min-h-[460px] w-full"
    : "h-[410px] min-h-[360px] w-full";
  const cartesianStageClassName = isWorkspacePresentation
    ? "min-h-[500px] w-full"
    : "h-[430px] min-h-[380px] w-full";
  const axisLabels = chartAxisLabels(option);
  const tooltipStyle = {
    backgroundColor: palette.chartPanelBgClassName.includes("071112")
      ? "#071112"
      : "#FFFFFF",
    border: `1px solid ${palette.chartGridColor}`,
    borderRadius: 8,
    color: palette.chartLegendColor,
    fontSize: 12,
  };
  const tooltipLabelStyle = {
    color: palette.chartLegendColor,
    fontWeight: 700,
  };
  const tickStyle = {
    fill: palette.chartAxisColor,
    fontSize: 12,
    fontWeight: 600,
  };

  if (option.chartType === "pie") {
    const legendItems = chartPoints.map((point, index) => ({
      key: point.key,
      label: point.label || point.bucket || point.key,
      color: CHART_COLORS[index % CHART_COLORS.length],
      value: formatChartValue(finiteChartPointValue(point)),
    }));

    return renderRechartsStageFrame({
      className: aggregateStageClassName,
      style: aggregateStageStyle,
      legendItems,
      palette,
      children: (
        <ResponsiveContainer width="100%" height="100%">
          <RechartsPieChart margin={{ top: 20, right: 24, left: 24, bottom: 20 }}>
            <Tooltip
              formatter={tooltipFormatter}
              labelFormatter={tooltipLabelFormatter}
              contentStyle={tooltipStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={{ color: palette.chartLegendColor }}
            />
            <Pie
              data={chartPoints}
              dataKey="value"
              nameKey="label"
              innerRadius={72}
              outerRadius={132}
              paddingAngle={2}
              {...animationProps}
            >
              {chartPoints.map((point, index) => (
                <Cell
                  key={point.key}
                  fill={CHART_COLORS[index % CHART_COLORS.length]}
                />
              ))}
            </Pie>
          </RechartsPieChart>
        </ResponsiveContainer>
      ),
    });
  }

  if (option.chartType === "bar" || option.chartType === "line") {
    const { data, series } = buildRechartsCartesianDataset(option, chartPoints);
    const seriesLabelByKey = new Map(series.map((item) => [item.key, item.label]));
    const cartesianTooltipFormatter = (value: unknown, name: unknown) => [
      formatChartValue(typeof value === "number" ? value : Number(value)),
      seriesLabelByKey.get(String(name ?? "")) ?? String(name ?? "Value"),
    ];
    const xTickAngle = data.length > 8 ? -22 : 0;
    const xTickAnchor = xTickAngle === 0 ? "middle" : "end";
    const commonAxisProps = {
      tickLine: { stroke: palette.chartGridColor },
      axisLine: { stroke: palette.chartGridColor },
    };
    const legendItems = series.map((item) => ({
      key: item.key,
      label: item.label,
      color: item.color,
    }));

    return renderRechartsStageFrame({
      className: cartesianStageClassName,
      style: cartesianStageStyle,
      legendItems,
      palette,
      children: (
        <ResponsiveContainer width="100%" height="100%">
          {option.chartType === "bar" ? (
            <RechartsBarChart
              data={data}
              margin={{ top: 20, right: 28, left: 38, bottom: 52 }}
              barGap={6}
              barCategoryGap={series.length > 1 ? "18%" : "34%"}
            >
              <CartesianGrid stroke={palette.chartGridColor} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                interval={data.length > 12 ? "preserveStartEnd" : 0}
                tick={tickStyle}
                angle={xTickAngle}
                textAnchor={xTickAnchor}
                tickFormatter={(label) => truncateChartLabel(label)}
                height={72}
                {...commonAxisProps}
              />
              <YAxis
                width={84}
                tick={tickStyle}
                tickFormatter={formatChartValue}
                {...commonAxisProps}
              />
              <Tooltip
                formatter={cartesianTooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                itemStyle={{ color: palette.chartLegendColor }}
              />
              {series.map((item) => (
                <Bar
                  key={item.key}
                  dataKey={item.key}
                  name={item.label}
                  fill={item.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={56}
                  {...animationProps}
                />
              ))}
            </RechartsBarChart>
          ) : (
            <RechartsLineChart
              data={data}
              margin={{ top: 20, right: 32, left: 38, bottom: 52 }}
            >
              <CartesianGrid stroke={palette.chartGridColor} strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                interval={data.length > 12 ? "preserveStartEnd" : 0}
                tick={tickStyle}
                angle={xTickAngle}
                textAnchor={xTickAnchor}
                tickFormatter={(label) => truncateChartLabel(label)}
                height={72}
                {...commonAxisProps}
              />
              <YAxis
                width={84}
                tick={tickStyle}
                tickFormatter={formatChartValue}
                {...commonAxisProps}
              />
              <Tooltip
                formatter={cartesianTooltipFormatter}
                labelFormatter={tooltipLabelFormatter}
                contentStyle={tooltipStyle}
                labelStyle={tooltipLabelStyle}
                itemStyle={{ color: palette.chartLegendColor }}
              />
              {series.map((item) => (
                <Line
                  key={item.key}
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stroke={item.color}
                  strokeWidth={3}
                  dot={{ r: 3, strokeWidth: 1.5, fill: item.color }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                  {...animationProps}
                />
              ))}
            </RechartsLineChart>
          )}
        </ResponsiveContainer>
      ),
    });
  }

  const scatterSeries = [
    ...chartPoints.reduce((groups, point) => {
      const key = point.seriesId || option.id;
      const current = groups.get(key) ?? {
        key,
        label: point.seriesLabel || chartOptionLabel(option),
        color: point.seriesColor || palette.chartSeriesColor,
        points: [] as ReportGenerationAdvancedVisualizationChartPoint[],
      };
      current.points.push(point);
      groups.set(key, current);
      return groups;
    }, new Map<string, {
      key: string;
      label: string;
      color: string;
      points: ReportGenerationAdvancedVisualizationChartPoint[];
    }>()).values(),
  ];
  const scatterLegendItems = scatterSeries.map((item) => ({
    key: item.key,
    label: item.label,
    color: item.color,
    value: `${item.points.length} point${item.points.length === 1 ? "" : "s"}`,
  }));

  return renderRechartsStageFrame({
    className: aggregateStageClassName,
    style: aggregateStageStyle,
    legendItems: scatterLegendItems,
    palette,
    children: (
      <ResponsiveContainer width="100%" height="100%">
        <RechartsScatterChart
          margin={{ top: 20, right: 28, left: 38, bottom: 52 }}
        >
          <CartesianGrid stroke={palette.chartGridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            name={axisLabels.x}
            tick={tickStyle}
            tickFormatter={formatChartValue}
            width={72}
            tickLine={{ stroke: palette.chartGridColor }}
            axisLine={{ stroke: palette.chartGridColor }}
          />
          <YAxis
            dataKey="y"
            name={axisLabels.y}
            tick={tickStyle}
            tickFormatter={formatChartValue}
            width={84}
            tickLine={{ stroke: palette.chartGridColor }}
            axisLine={{ stroke: palette.chartGridColor }}
          />
          <Tooltip
            formatter={tooltipFormatter}
            labelFormatter={tooltipLabelFormatter}
            contentStyle={tooltipStyle}
            labelStyle={tooltipLabelStyle}
            itemStyle={{ color: palette.chartLegendColor }}
            cursor={{
              stroke: palette.chartScatterCursorColor,
              strokeDasharray: "3 3",
            }}
          />
          {scatterSeries.map((item) => (
            <Scatter
              key={item.key}
              name={item.label}
              data={item.points}
              fill={item.color}
              {...animationProps}
            />
          ))}
        </RechartsScatterChart>
      </ResponsiveContainer>
    ),
  });
};

const renderChartAxisLabelBadges = (
  option: ReportGenerationAdvancedVisualizationChartOption | null,
  palette: SurfaceThemePalette,
  mode: "core" | "advanced",
) => {
  const labels = chartAxisLabels(option);
  const entries = [
    { key: "x", label: "X-axis", value: labels.x },
    { key: "y", label: "Y-axis", value: labels.y },
    ...(labels.series ? [{ key: "series", label: "Series", value: labels.series }] : []),
  ];
  return (
    <div
      className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3"
      data-testid={`report-generation-advanced-visualization-chart-axis-labels-${mode}`}
    >
      {entries.map((entry) => (
        <div
          key={entry.key}
          className={joinClasses(
            "min-w-0 rounded-md border px-3 py-2",
            palette.accentChipBorderClassName,
            palette.accentChipBgClassName,
          )}
          data-testid="report-generation-advanced-visualization-chart-axis-label"
          data-axis={entry.key}
          title={`${entry.label}: ${entry.value}`}
          aria-label={`${entry.label}: ${entry.value}`}
        >
          <p className={joinClasses("text-[10px] font-semibold uppercase tracking-[0.12em]", palette.accentChipTextClassName)}>
            {entry.label}
          </p>
          <p
            className={joinClasses(
              "mt-1 min-h-[2.5rem] max-h-[2.5rem] overflow-hidden text-sm font-semibold leading-5",
              palette.summaryValueTextClassName,
            )}
            style={{ overflowWrap: "anywhere" }}
          >
            {entry.value}
          </p>
        </div>
      ))}
    </div>
  );
};

export function ReportGenerationAdvancedVisualization({
  snapshot,
  onClose,
  initialView,
  activeView: controlledActiveView,
  onActiveViewChange,
  initialChartType,
  activeChartType: controlledActiveChartType,
  onActiveChartTypeChange,
  surfaceTheme = "light",
  presentation = "inline",
}: ReportGenerationAdvancedVisualizationProps) {
  const containerRef = useRef<HTMLElement | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const chartMountRef = useRef<HTMLDivElement | null>(null);
  const graphControllerRef = useRef<DataVizRendererController | null>(null);
  const chartControllerRef = useRef<DataVizRendererController | null>(null);
  const [rendererState, setRendererState] = useState<RendererState>("loading");
  const [chartRendererState, setChartRendererState] =
    useState<RendererState>("unavailable");
  const palette = REPORT_GENERATION_SURFACE_THEME_PALETTES[surfaceTheme];
  const graphSceneTheme = useMemo(
    () => buildReportGenerationDataVizTheme(surfaceTheme, palette, "graph"),
    [palette, surfaceTheme],
  );
  const chartSceneTheme = useMemo(
    () => buildReportGenerationDataVizTheme(surfaceTheme, palette, "chart"),
    [palette, surfaceTheme],
  );
  const isWorkspacePresentation = presentation === "workspace";
  const chartSnapshot = snapshot.chartVisualization ?? null;
  const chartOptions = chartSnapshot?.chartOptions ?? [];
  const visualizationPolicy = chartSnapshot?.policy ?? null;
  const policyDefaultView = visualizationPolicy?.defaultView ?? "chart";
  const policyDefaultBodySurface = visualizationPolicy?.defaultBodySurface ??
    (chartOptions.length > 0 ? "visual" : "table");
  const policyUnmappedFallback = visualizationPolicy?.unmappedFallback ?? "table";
  const policyFallbackReason = visualizationPolicy?.fallbackReason ?? "";
  const policyCoverageSignature = visualizationPolicy?.coverageSignature ?? "";
  const policyDefaultChartOptionId = visualizationPolicy?.defaultChartOptionId ?? "";
  const graphViewEnabled = visualizationPolicy?.graphViewEnabled === true;
  const graphDefaultAllowed = graphViewEnabled && visualizationPolicy?.graphDefaultAllowed === true;
  const compatibleChartTypes = useMemo(
    () =>
      (["bar", "line", "pie", "scatter"] as const).filter((chartType) =>
        chartOptions.some((option) => option.chartType === chartType),
      ),
    [chartOptions],
  );
  const advancedChartTypes = useMemo(
    () =>
      (["bar", "line"] as const).filter((chartType) =>
        chartOptions.some((option) => option.chartType === chartType && isReportGenerationThreeChartType(option.chartType)),
      ),
    [chartOptions],
  );
  const hasCoreCharts = chartOptions.length > 0;
  const hasAdvancedCharts = advancedChartTypes.length > 0;
  const resolvedInitialView =
    initialView === "graph" && graphViewEnabled
      ? "graph"
      : initialView === "advanced" && hasAdvancedCharts
        ? "advanced"
        : hasCoreCharts
          ? policyDefaultView === "advanced" && hasAdvancedCharts
            ? "advanced"
            : "chart"
          : graphDefaultAllowed
            ? "graph"
            : "chart";
  const [uncontrolledActiveView, setUncontrolledActiveView] =
    useState<ActiveView>(resolvedInitialView);
  const requestedActiveView = controlledActiveView ?? uncontrolledActiveView;
  const activeView =
    requestedActiveView === "graph" && !graphViewEnabled
      ? GRAPH_DISABLED_FALLBACK_VIEW
      : requestedActiveView;
  const setResolvedActiveView = useCallback((nextView: ActiveView) => {
    if (controlledActiveView === undefined) {
      setUncontrolledActiveView(nextView);
    }
    onActiveViewChange?.(nextView);
  }, [controlledActiveView, onActiveViewChange]);
  const [uncontrolledActiveChartType, setUncontrolledActiveChartType] =
    useState<ReportGenerationAdvancedVisualizationChartType>(
      initialChartType ?? chartSnapshot?.defaultChartType ?? "bar",
    );
  const activeChartType =
    controlledActiveChartType ?? uncontrolledActiveChartType;
  const setResolvedActiveChartType = useCallback((
    nextChartType: ReportGenerationAdvancedVisualizationChartType,
  ) => {
    if (controlledActiveChartType === undefined) {
      setUncontrolledActiveChartType(nextChartType);
    }
    onActiveChartTypeChange?.(nextChartType);
  }, [controlledActiveChartType, onActiveChartTypeChange]);
  const [activeChartOptionId, setActiveChartOptionId] = useState<string | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const [graphAutoRotate, setGraphAutoRotate] = useState(!reducedMotion);
  const [graphSceneSize] = useState<GraphSceneSize>("standard");
  const [graphSceneHeight, setGraphSceneHeight] = useState(
    GRAPH_SCENE_SIZE_PRESETS.standard,
  );
  const [graphDesktopLayout, setGraphDesktopLayout] = useState(false);
  const [graphCameraPosition, setGraphCameraPosition] = useState("0.00,0.00,420.00");
  const [graphCameraTarget, setGraphCameraTarget] = useState("0.00,0.00,0.00");
  const [chartCameraPosition, setChartCameraPosition] = useState("0.00,82.00,268.00");
  const [chartCameraTarget, setChartCameraTarget] = useState("0.00,0.00,0.00");
  const graphAutoRotateRef = useRef(graphAutoRotate);
  const graph = useMemo(
    () => projectReportGenerationAdvancedVisualizationGraph(snapshot),
    [snapshot],
  );
  const visibleChartTypes = activeView === "advanced"
    ? advancedChartTypes
    : compatibleChartTypes;
  const activeChartOptionsForType = useMemo(
    () => chartOptions.filter((option) => option.chartType === activeChartType),
    [activeChartType, chartOptions],
  );
  const activeChartOption = useMemo(
    () =>
      chartOptions.find((option) => option.id === activeChartOptionId && option.chartType === activeChartType) ??
      activeChartOptionsForType[0] ??
      chartOptions[0] ??
      null,
    [activeChartOptionId, activeChartOptionsForType, activeChartType, chartOptions],
  );
  const activeChartPoints = useMemo(
    () =>
      activeChartOption
        ? (chartSnapshot?.series ?? []).filter(
            (point) => point.optionId === activeChartOption.id,
          )
        : [],
    [activeChartOption, chartSnapshot?.series],
  );
  const activeThreeChart = useMemo<DataVizChart | null>(
    () =>
      activeChartOption
        ? projectReportGenerationAdvancedVisualizationChart(
          activeChartOption,
          activeChartPoints,
        )
        : null,
    [activeChartOption, activeChartPoints],
  );
  const activeChartProjectionSignature = useMemo(
    () =>
      [
        activeChartOption?.chartType ?? "none",
        activeChartOption?.renderKind ?? activeChartOption?.chartType ?? "none",
        activeChartOption?.useCaseId ?? activeChartOption?.id ?? "none",
        activeChartOption?.metricFieldId ?? "rows",
        activeChartOption?.dimensionFieldId ?? "aggregate",
        activeChartOption?.groupFieldId ?? "group",
        activeChartOption?.xFieldId ?? "x",
        activeChartOption?.yFieldId ?? "y",
        activeChartPoints
          .map((point) =>
            [
              point.seriesId ?? "",
              point.seriesLabel ?? "",
              point.key,
              point.label,
              point.value ?? "",
              point.sum ?? "",
              point.average ?? "",
              point.count,
            ].join("="),
          )
          .join("|"),
      ].join("::"),
    [activeChartOption, activeChartPoints],
  );
  const activeChartBucketSignature = useMemo(
    () =>
      activeChartPoints
        .map((point) => point.label || point.bucket || point.key)
        .filter(Boolean)
        .join("|"),
    [activeChartPoints],
  );
  const activeThreeChartPoints = useMemo(
    () =>
      activeThreeChart?.series.flatMap((series) =>
        series.points.map((point) => ({ point, series })),
      ) ?? [],
    [activeThreeChart],
  );
  const activeRechartsDataset = useMemo<{
    data: RechartsCartesianPoint[];
    series: RechartsSeriesDefinition[];
  }>(
    () =>
      activeChartOption
        ? buildRechartsCartesianDataset(activeChartOption, activeChartPoints)
        : { data: [], series: [] },
    [activeChartOption, activeChartPoints],
  );
  const activeViewUsesAdvancedChart = activeView === "advanced";
  const chartRenderKind =
    activeViewUsesAdvancedChart && activeThreeChart
      ? activeThreeChart.kind
      : activeChartOption?.renderKind ?? activeChartOption?.chartType ?? "none";
  const chartSeriesCount =
    activeViewUsesAdvancedChart && activeThreeChart
      ? activeThreeChart.series.length
      : activeChartOption?.chartType === "bar" || activeChartOption?.chartType === "line"
        ? activeRechartsDataset.series.length
        : activeChartOption && activeChartPoints.length > 0
          ? 1
          : 0;
  const chartValueTotal = useMemo(
    () =>
      activeViewUsesAdvancedChart && activeThreeChart
        ? activeThreeChart.series
        .flatMap((series) => series.points)
        .reduce((total, point) => total + point.value, 0)
        : activeChartPoints.reduce((total, point) => {
        const value = typeof point.value === "number" && Number.isFinite(point.value)
          ? point.value
          : 0;
        return total + value;
      }, 0),
    [activeChartPoints, activeThreeChart, activeViewUsesAdvancedChart],
  );
  const chartRenderer =
    activeViewUsesAdvancedChart && activeChartOption && isReportGenerationThreeChartType(activeChartOption.chartType)
      ? activeThreeChart ? "threejs" : "none"
      : activeChartOption && activeChartPoints.length > 0
        ? "recharts"
        : "none";
  const chartState =
    chartOptions.length === 0
      ? "unavailable"
      : activeChartPoints.length > 0
        ? "ready"
        : "empty";
  const schemaChips = useMemo(
    () =>
      [
        ...new Set(
          (chartSnapshot?.columns ?? [])
            .map((column) => column.domainSchema ?? column.domainType)
            .filter((value): value is string => Boolean(value)),
        ),
      ].sort(),
    [chartSnapshot?.columns],
  );
  const technicalCorrelationGroups = useMemo(
    () => [
      {
        id: "schema",
        label: "Schemas",
        nodes: graph.nodes.filter((node) => node.kind === "schema"),
      },
      {
        id: "function",
        label: "Functions",
        nodes: graph.nodes.filter((node) => node.kind === "function"),
      },
      {
        id: "pivot",
        label: "Pivots",
        nodes: graph.nodes.filter((node) => node.kind === "pivot"),
      },
      {
        id: "join",
        label: "Links",
        nodes: graph.nodes.filter((node) => node.kind === "join"),
      },
      {
        id: "field",
        label: "Fields",
        nodes: graph.nodes.filter((node) => node.kind === "field").slice(0, 4),
      },
    ],
    [graph.nodes],
  );
  const graphSchemaCount = graph.nodes.filter((node) => node.kind === "schema").length;
  const graphFunctionCount = graph.nodes.filter((node) => node.kind === "function").length;
  const graphPivotCount = graph.nodes.filter((node) => node.kind === "pivot").length;
  const graphLegendItems = useMemo(
    () => [
      { kind: "datasource" as const, label: "Source" },
      { kind: "function" as const, label: "Service" },
      { kind: "operation" as const, label: "Query" },
      { kind: "schema" as const, label: "Record type" },
      { kind: "entity" as const, label: "Record group" },
      { kind: "field" as const, label: "Field" },
      { kind: "join" as const, label: "Link" },
      { kind: "pivot" as const, label: "Match key" },
    ],
    [],
  );
  const businessEntityLabels = useMemo(
    () =>
      uniqueSorted(
        snapshot.modules.flatMap((module) =>
          module.entities
            .filter((entity) => entity.selected)
            .map((entity) => entity.label),
        ),
      ),
    [snapshot.modules],
  );
  const businessFieldLabels = useMemo(
    () => {
      const chartFieldLabels = chartSnapshot?.columns.map((column) => column.label) ?? [];
      if (chartFieldLabels.length > 0) {
        return uniqueSorted(chartFieldLabels);
      }
      return uniqueSorted(
        snapshot.modules.flatMap((module) =>
          module.entities.flatMap((entity) => entity.fields.map((field) => field.label)),
        ),
      );
    },
    [chartSnapshot?.columns, snapshot.modules],
  );
  const businessDatasourceLabels = useMemo(
    () => {
      const sourceFamilies = snapshot.datasourcePlan?.selectedDatasourceFamilies?.length
        ? snapshot.datasourcePlan.selectedDatasourceFamilies
        : snapshot.datasourceFamilies;
      return uniqueSorted(sourceFamilies.map(humanizeDatasourceFamily));
    },
    [snapshot.datasourceFamilies, snapshot.datasourcePlan?.selectedDatasourceFamilies],
  );
  const businessJoinNarratives = useMemo(
    () =>
      snapshot.joins.length > 0
        ? snapshot.joins.map((join) => formatJoinNarrative(join.label))
        : ["No cross-record links are active for this report."],
    [snapshot.joins],
  );
  const accessSummary = useMemo(() => {
    if (snapshot.blockedEntityIds.length > 0) {
      return `Some parts of this report are hidden by access rules (${snapshot.blockedEntityIds.length} blocked).`;
    }
    return "You can access every part of this report preview.";
  }, [snapshot.blockedEntityIds]);
  const audienceSummary = useMemo(
    () => `${formatPersonaLabel(snapshot.actor.persona)} view`,
    [snapshot.actor.persona],
  );
  const graphSceneSizeLabel = GRAPH_SCENE_SIZE_LABELS[graphSceneSize];

  useEffect(() => {
    graphAutoRotateRef.current = graphAutoRotate;
  }, [graphAutoRotate]);

  useEffect(() => {
    if (reducedMotion) {
      setGraphAutoRotate(false);
    }
  }, [reducedMotion]);

  const pauseGraphAutoRotate = useCallback(() => {
    if (!graphAutoRotateRef.current) {
      return;
    }
    graphAutoRotateRef.current = false;
    setGraphAutoRotate(false);
  }, []);

  const syncGraphSceneSizing = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }
    const stage = mountRef.current;
    const containerWidth =
      containerRef.current?.getBoundingClientRect().width ?? window.innerWidth;
    const desktopLayout = containerWidth >= GRAPH_DESKTOP_BREAKPOINT;
    setGraphDesktopLayout(desktopLayout);
    if (!stage) {
      return;
    }
    const presetHeight = GRAPH_SCENE_SIZE_PRESETS[graphSceneSize];
    const stageTop = stage.getBoundingClientRect().top;
    const availableViewportHeight =
      window.innerHeight - stageTop - GRAPH_SCENE_VIEWPORT_GUTTER;
    const resolvedHeight = Math.max(
      GRAPH_SCENE_MIN_HEIGHT,
      Math.min(
        presetHeight,
        Math.max(availableViewportHeight, GRAPH_SCENE_MIN_HEIGHT),
      ),
    );
    setGraphSceneHeight((currentHeight) =>
      currentHeight === resolvedHeight ? currentHeight : resolvedHeight,
    );
  }, [graphSceneSize]);

  useEffect(() => {
    syncGraphSceneSizing();
    window.addEventListener("resize", syncGraphSceneSizing);
    const container = containerRef.current;
    const observer =
      typeof ResizeObserver !== "undefined" && container
        ? new ResizeObserver(syncGraphSceneSizing)
        : null;
    observer?.observe(container as Element);
    return () => {
      window.removeEventListener("resize", syncGraphSceneSizing);
      observer?.disconnect();
    };
  }, [activeView, syncGraphSceneSizing]);

  useEffect(() => {
    if (requestedActiveView === "graph" && !graphViewEnabled) {
      setResolvedActiveView(GRAPH_DISABLED_FALLBACK_VIEW);
    }
  }, [
    graphViewEnabled,
    requestedActiveView,
    setResolvedActiveView,
  ]);

  useEffect(() => {
    if (chartOptions.length === 0) {
      if (activeView === "advanced") {
        setResolvedActiveView("chart");
      }
      setActiveChartOptionId(null);
      return;
    }
    if (activeView === "advanced" && !hasAdvancedCharts) {
      setResolvedActiveView("chart");
      return;
    }
    if (
      activeView === "advanced" &&
      advancedChartTypes.length > 0 &&
      !advancedChartTypes.includes(activeChartType as "bar" | "line")
    ) {
      const nextChartType = advancedChartTypes[0];
      setResolvedActiveChartType(nextChartType);
      setActiveChartOptionId(
        chartOptions.find((option) => option.chartType === nextChartType)?.id ?? null,
      );
      return;
    }
    if (!chartOptions.some((option) => option.chartType === activeChartType)) {
      setResolvedActiveChartType(
        chartSnapshot?.defaultChartType ?? chartOptions[0]?.chartType ?? "bar",
      );
      setActiveChartOptionId(null);
      return;
    }
    if (
      activeChartOptionId &&
      chartOptions.some((option) => option.id === activeChartOptionId && option.chartType === activeChartType)
    ) {
      return;
    }
    setActiveChartOptionId(
      chartOptions.find((option) => option.chartType === activeChartType)?.id ??
        chartOptions[0]?.id ??
        null,
    );
  }, [
    activeChartOptionId,
    activeChartType,
    activeView,
    advancedChartTypes,
    chartOptions,
    chartSnapshot?.defaultChartType,
    hasAdvancedCharts,
    setResolvedActiveChartType,
    setResolvedActiveView,
  ]);

  useEffect(() => {
    const mount = mountRef.current;
    if (activeView !== "graph") {
      graphControllerRef.current = null;
      setRendererState("unavailable");
      return undefined;
    }
    if (!mount || typeof window === "undefined") {
      graphControllerRef.current = null;
      setRendererState("unavailable");
      return undefined;
    }

    let cancelled = false;
    let cleanupScene: (() => void) | null = null;
    setRendererState("loading");

    void Promise.all([
      import("three"),
      import("three/examples/jsm/controls/OrbitControls.js"),
    ])
      .then(([THREE, controlsModule]) => {
        if (cancelled || !mountRef.current) {
          return;
        }
        const { OrbitControls } = controlsModule;
        const target = mountRef.current;
        const handleDiagnostics = (diagnostics: DataVizCameraDiagnostics) => {
          setGraphCameraPosition(diagnostics.cameraPosition);
          setGraphCameraTarget(diagnostics.cameraTarget);
        };
        const controller = mountDataVizThreeScene({
          THREE,
          OrbitControls,
          mountElement: target,
          graph,
          testId: "report-generation-advanced-visualization-canvas",
          ariaLabel: "Report schema visualization",
          reducedMotion,
          autoRotate: () => graphAutoRotateRef.current,
          onPauseAutoRotate: pauseGraphAutoRotate,
          onDiagnosticsChange: handleDiagnostics,
          theme: graphSceneTheme,
        });
        const canvas = target.querySelector("canvas");
        canvas?.setAttribute("data-chart-scene-theme", surfaceTheme);
        canvas?.setAttribute("data-chart-scene-background", graphSceneTheme.background ?? "");
        graphControllerRef.current = controller;
        cleanupScene = () => {
          graphControllerRef.current = null;
          controller.dispose();
        };
        setRendererState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setRendererState("error");
        }
      });

    return () => {
      cancelled = true;
      cleanupScene?.();
    };
  }, [activeView, graph, graphSceneTheme, reducedMotion, pauseGraphAutoRotate, surfaceTheme]);

  useEffect(() => {
    const mount = chartMountRef.current;
    if (activeView !== "advanced" || !activeThreeChart) {
      chartControllerRef.current = null;
      setChartRendererState("unavailable");
      return undefined;
    }
    if (!mount || typeof window === "undefined") {
      chartControllerRef.current = null;
      setChartRendererState("unavailable");
      return undefined;
    }

    let cancelled = false;
    let cleanupScene: (() => void) | null = null;
    setChartRendererState("loading");

    void Promise.all([
      import("three"),
      import("three/examples/jsm/controls/OrbitControls.js"),
    ])
      .then(([THREE, controlsModule]) => {
        if (cancelled || !chartMountRef.current) {
          return;
        }
        const { OrbitControls } = controlsModule;
        const target = chartMountRef.current;
        const handleDiagnostics = (diagnostics: DataVizCameraDiagnostics) => {
          setChartCameraPosition(diagnostics.cameraPosition);
          setChartCameraTarget(diagnostics.cameraTarget);
        };
        const controller = mountDataVizThreeChartScene({
          THREE,
          OrbitControls,
          mountElement: target,
          chart: activeThreeChart,
          testId: "report-generation-advanced-visualization-chart-canvas",
          ariaLabel: `${activeThreeChart.kind} report aggregate chart`,
          reducedMotion,
          onDiagnosticsChange: handleDiagnostics,
          theme: chartSceneTheme,
        });
        const canvas = target.querySelector("canvas");
        canvas?.setAttribute("data-chart-scene-theme", surfaceTheme);
        canvas?.setAttribute("data-chart-scene-background", chartSceneTheme.background ?? "");
        chartControllerRef.current = controller;
        cleanupScene = () => {
          chartControllerRef.current = null;
          controller.dispose();
        };
        setChartRendererState("ready");
      })
      .catch(() => {
        if (!cancelled) {
          setChartRendererState("error");
        }
      });

    return () => {
      cancelled = true;
      cleanupScene?.();
    };
  }, [activeChartProjectionSignature, activeThreeChart, activeView, chartSceneTheme, reducedMotion, surfaceTheme]);

  const toggleGraphAutoRotate = useCallback(() => {
    const nextValue = !graphAutoRotateRef.current;
    graphAutoRotateRef.current = nextValue;
    setGraphAutoRotate(nextValue);
  }, []);

  const resetGraphView = useCallback(() => {
    pauseGraphAutoRotate();
    graphControllerRef.current?.resetView();
  }, [pauseGraphAutoRotate]);

  const selectChartType = useCallback((chartType: ReportGenerationAdvancedVisualizationChartType) => {
    setResolvedActiveChartType(chartType);
    setActiveChartOptionId(
      chartOptions.find((option) => option.chartType === chartType)?.id ?? null,
    );
  }, [chartOptions, setResolvedActiveChartType]);

  const selectActiveView = useCallback((nextView: ActiveView) => {
    if (nextView === "graph" && !graphViewEnabled) {
      return;
    }
    if (nextView === "advanced") {
      const nextChartType = advancedChartTypes.includes(activeChartType as "bar" | "line")
        ? activeChartType
        : advancedChartTypes[0];
      if (nextChartType) {
        selectChartType(nextChartType);
      }
    }
    setResolvedActiveView(nextView);
  }, [activeChartType, advancedChartTypes, graphViewEnabled, selectChartType, setResolvedActiveView]);

  const activeChartRendererState =
    chartRenderer === "threejs"
      ? chartRendererState
      : chartRenderer === "recharts"
        ? "ready"
        : "unavailable";
  const rootRendererState = activeView === "graph"
    ? rendererState
    : activeChartRendererState;
  const headerSummary = activeView === "graph"
    ? `${graph.nodes.length} nodes / ${graph.edges.length} links / ${snapshot.graphqlOperations.length} operations`
    : `${activeChartPoints.length} points / ${chartSeriesCount} ${chartSeriesCount === 1 ? "series" : "series"} / ${formatChartValue(chartValueTotal)} total`;

  const renderSideRail = () => {
    if (activeView !== "graph" && chartSnapshot) {
      return (
        <div className="space-y-4">
          <div>
            <p
              className={joinClasses(
                "text-[11px] font-semibold uppercase tracking-[0.14em]",
                palette.accentTextClassName,
              )}
            >
              Schemas
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {schemaChips.length > 0 ? (
                schemaChips.map((schema) => (
                  <span
                    key={schema}
                    className={joinClasses(
                      "rounded-full border px-2 py-1 text-[10px] font-semibold",
                      palette.accentChipBorderClassName,
                      palette.accentChipBgClassName,
                      palette.accentChipTextClassName,
                    )}
                    data-testid="report-generation-advanced-visualization-schema-chip"
                  >
                    {schema}
                  </span>
                ))
              ) : (
                <span className={joinClasses("text-xs", palette.summaryTextClassName)}>
                  No schema chips
                </span>
              )}
            </div>
          </div>
          <div>
            <p
              className={joinClasses(
                "text-[11px] font-semibold uppercase tracking-[0.14em]",
                palette.accentTextClassName,
              )}
            >
              Entity links
            </p>
            <div className="mt-2 space-y-2">
              {chartSnapshot.entityLinks.length > 0 ? (
                chartSnapshot.entityLinks.map((link) => (
                  <div
                    key={link.id}
                    className={joinClasses(
                      "rounded-md border px-2 py-1.5 text-[11px]",
                      palette.railCardBorderClassName,
                      palette.railCardTextClassName,
                    )}
                    data-testid="report-generation-advanced-visualization-entity-link"
                  >
                    <p className="font-semibold">{link.label}</p>
                    {link.joinKey ? (
                      <p className={joinClasses("mt-0.5", palette.summaryTextClassName)}>
                        {link.joinKey}
                      </p>
                    ) : null}
                  </div>
                ))
              ) : (
                <span className={joinClasses("text-xs", palette.summaryTextClassName)}>
                  No active joins
                </span>
              )}
            </div>
          </div>
          <div className={joinClasses("grid grid-cols-2 gap-2 text-xs", palette.summaryTextClassName)}>
            <p>
              <span className={joinClasses("font-semibold", palette.summaryValueTextClassName)}>
                {chartSnapshot.summary.selectedRowCount}
              </span>{" "}
              rows
            </p>
            <p>
              <span className={joinClasses("font-semibold", palette.summaryValueTextClassName)}>
                {chartSnapshot.summary.visibleColumnCount}
              </span>{" "}
              columns
            </p>
            <p>
              <span className={joinClasses("font-semibold", palette.summaryValueTextClassName)}>
                {chartSnapshot.summary.numericFieldCount}
              </span>{" "}
              numeric
            </p>
            <p>
              <span className={joinClasses("font-semibold", palette.summaryValueTextClassName)}>
                {chartSnapshot.summary.categoryFieldCount}
              </span>{" "}
              category
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        <div
          className="space-y-3"
          data-testid="report-generation-advanced-visualization-graph-business-map"
        >
          <p
            className={joinClasses(
              "text-[11px] font-semibold uppercase tracking-[0.14em]",
              palette.accentTextClassName,
            )}
          >
            Report map
          </p>
          <div
            className={joinClasses(
              "rounded-md border px-2 py-1.5 text-[11px]",
              palette.railCardBorderClassName,
              palette.railCardTextClassName,
            )}
          >
            <p className="font-semibold">{audienceSummary}</p>
            <p className={joinClasses("mt-0.5", palette.summaryTextClassName)}>
              {accessSummary}
            </p>
          </div>
          {[
            { label: "Entities", values: businessEntityLabels },
            { label: "Fields", values: businessFieldLabels },
            { label: "Sources", values: businessDatasourceLabels },
            { label: "Links", values: businessJoinNarratives },
          ].map((group) => (
            <div key={group.label}>
              <p className={joinClasses("text-[10px] font-semibold uppercase tracking-[0.12em]", palette.summaryTextClassName)}>
                {group.label}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {group.values.slice(0, 4).map((value) => (
                  <span
                    key={value}
                    className={joinClasses(
                      "rounded-full border px-2 py-1 text-[10px] font-semibold",
                      palette.accentChipBorderClassName,
                      palette.accentChipBgClassName,
                      palette.accentChipTextClassName,
                    )}
                  >
                    {value}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-3">
          <p
            className={joinClasses(
              "text-[11px] font-semibold uppercase tracking-[0.14em]",
              palette.accentTextClassName,
            )}
          >
            Graph correlations
          </p>
          {technicalCorrelationGroups.map((group) => {
            const testIdKind = group.id === "join" ? "link" : group.id;
            return (
              <div key={group.id}>
                <p className={joinClasses("text-[10px] font-semibold uppercase tracking-[0.12em]", palette.summaryTextClassName)}>
                  {group.label}
                </p>
                <div className="mt-1.5 space-y-1.5">
                  {group.nodes.length > 0 ? (
                    group.nodes.map((node) => (
                      <div
                        key={node.id}
                        className={joinClasses(
                          "rounded-md border px-2 py-1.5 text-[11px]",
                          palette.railCardBorderClassName,
                          palette.railCardTextClassName,
                        )}
                        data-testid={`report-generation-advanced-visualization-graph-${testIdKind}`}
                      >
                        <p className="font-semibold">{node.label}</p>
                        {node.detail ? (
                          <p className={joinClasses("mt-0.5", palette.summaryTextClassName)}>
                            {node.detail}
                          </p>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <span className={joinClasses("text-xs", palette.summaryTextClassName)}>
                      None
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={joinClasses("mt-4 grid grid-cols-2 gap-2 text-xs", palette.summaryTextClassName)}>
          <p>
            <span className={joinClasses("font-semibold", palette.summaryValueTextClassName)}>
              {snapshot.actor.persona}
            </span>{" "}
            via {snapshot.actor.groups.length} Cognito group
            {snapshot.actor.groups.length === 1 ? "" : "s"}
          </p>
          <p>
            {snapshot.datasourceFamilies.length} datasource famil
            {snapshot.datasourceFamilies.length === 1 ? "y" : "ies"} through{" "}
            {snapshot.executionAdapters.length || 1} adapter
            {snapshot.executionAdapters.length === 1 ? "" : "s"}
          </p>
          <p>
            {snapshot.visibleColumnCount} visible columns from{" "}
            {snapshot.selectedEntityCount} selected entities
          </p>
          <p>
            {graphPivotCount} pivot{graphPivotCount === 1 ? "" : "s"} /{" "}
            {graphFunctionCount} function{graphFunctionCount === 1 ? "" : "s"}
          </p>
          <p>{graphSceneSizeLabel} graph</p>
        </div>
        <div
          className="mt-3 flex flex-wrap gap-1.5"
          data-testid="report-generation-advanced-visualization-graph-node-key"
        >
          {graphLegendItems.map(({ kind, label }) => (
            <span
              key={kind}
              className={joinClasses(
                "inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-semibold capitalize",
                palette.railCardBorderClassName,
                palette.summaryTextClassName,
              )}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: NODE_COLORS[kind] ?? "#64748B",
                }}
              />
              {label}
            </span>
          ))}
        </div>
      </>
    );
  };

  return (
    <section
      ref={containerRef}
      className={joinClasses(
        isWorkspacePresentation
          ? "flex min-h-[640px] w-full flex-1 flex-col overflow-hidden rounded-lg border"
          : "mx-auto w-full max-w-[1180px] overflow-hidden rounded-lg border",
        palette.containerBorderClassName,
        palette.containerBgClassName,
        palette.containerShadowClassName,
      )}
      data-testid="report-generation-advanced-visualization-container"
      data-report-generation-surface-theme={surfaceTheme}
      data-presentation={presentation}
      data-active-view={activeView}
      data-node-count={graph.nodes.length}
      data-edge-count={graph.edges.length}
      data-operation-count={snapshot.graphqlOperations.length}
      data-graph-schema-count={graphSchemaCount}
      data-graph-function-count={graphFunctionCount}
      data-graph-pivot-count={graphPivotCount}
      data-graph-layout={graphDesktopLayout ? "desktop" : "stacked"}
      data-graph-camera-position={graphCameraPosition}
      data-graph-camera-target={graphCameraTarget}
      data-graph-scene-theme={surfaceTheme}
      data-graph-scene-background={graphSceneTheme.background ?? ""}
      data-renderer-state={rootRendererState}
      data-route={snapshot.pathname}
      data-datasource-plan-allowed={String(
        snapshot.datasourcePlan?.allowed ?? true,
      )}
      data-chart-state={chartState}
      data-chart-type={activeChartOption?.chartType ?? "none"}
      data-chart-render-kind={chartRenderKind}
      data-chart-use-case-id={activeChartOption?.useCaseId ?? activeChartOption?.id ?? "none"}
      data-chart-template-id={activeChartOption?.templateId ?? "none"}
      data-chart-template-mapping-id={activeChartOption?.templateMappingId ?? "none"}
      data-chart-renderer={chartRenderer}
      data-chart-renderer-state={
        activeChartRendererState
      }
      data-chart-source="preview-aggregate"
      data-chart-actual-data={String(activeChartPoints.length > 0)}
      data-chart-animation-variant={
        activeChartOption ? resolveChartAnimationVariant(activeChartOption) : "none"
      }
      data-chart-animation-label={
        activeChartOption?.animationLabel ??
        (activeChartOption ? resolveChartAnimationVariant(activeChartOption) : "none")
      }
      data-chart-option-count={chartOptions.length}
      data-chart-policy-id={visualizationPolicy?.id ?? "legacy-implicit"}
      data-chart-policy-version={visualizationPolicy?.version ?? 0}
      data-chart-policy-default-body-surface={policyDefaultBodySurface}
      data-chart-policy-default-view={policyDefaultView}
      data-chart-policy-default-chart-option-id={policyDefaultChartOptionId}
      data-chart-policy-unmapped-fallback={policyUnmappedFallback}
      data-chart-policy-fallback-reason={policyFallbackReason}
      data-chart-policy-coverage-signature={policyCoverageSignature}
      data-chart-policy-graph-view-enabled={String(graphViewEnabled)}
      data-chart-policy-graph-default-allowed={String(graphDefaultAllowed)}
      data-chart-policy-matched-rules={visualizationPolicy?.matchedRuleIds.join("|") ?? ""}
      data-chart-policy-available-types={visualizationPolicy?.availableChartTypes.join("|") ?? ""}
      data-chart-point-count={activeChartPoints.length}
      data-chart-bucket-signature={activeChartBucketSignature}
      data-chart-series-count={chartSeriesCount}
      data-chart-value-total={chartValueTotal}
      data-chart-camera-position={chartCameraPosition}
      data-chart-camera-target={chartCameraTarget}
      data-chart-scene-theme={surfaceTheme}
      data-chart-scene-background={chartSceneTheme.background ?? ""}
      data-chart-projection-signature={activeChartProjectionSignature}
      data-chart-selected-field-signature={chartSnapshot?.summary.selectedFieldSignature ?? ""}
      data-chart-filter-signature={chartSnapshot?.summary.filterSignature ?? ""}
      data-chart-aggregate-only="true"
      data-chart-placeholder="false"
    >
      <div
        className={joinClasses(
          "flex items-center justify-between gap-4 border-b px-4 py-3",
          palette.sideRailBorderClassName,
          palette.headerBgClassName,
        )}
        data-testid="report-generation-advanced-visualization-header"
      >
        <div className="min-w-0">
          <p
            className={joinClasses(
              "text-[11px] font-semibold uppercase tracking-[0.16em]",
              palette.accentTextClassName,
            )}
          >
            Visualization
          </p>
          <p className={joinClasses("mt-1 text-xs", palette.headerTextSecondaryClassName)}>
            {headerSummary}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={joinClasses(
              "inline-flex rounded-md border p-0.5",
              palette.controlBorderClassName,
              palette.controlBgClassName,
            )}
            role="tablist"
            aria-label="Visualization views"
          >
            {chartOptions.length > 0 || activeView === "chart" ? (
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "chart"}
                onClick={() => selectActiveView("chart")}
                disabled={chartOptions.length === 0}
                className={joinClasses(
                  "inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                  activeView === "chart"
                    ? joinClasses(
                      palette.controlActiveBgClassName,
                      palette.controlActiveTextClassName,
                      "shadow-sm",
                    )
                    : joinClasses(
                      palette.controlInactiveTextClassName,
                      palette.controlInactiveHoverTextClassName,
                    ),
                )}
              >
                <BarChart3 size={14} />
                <span>Visualization</span>
              </button>
            ) : null}
            {hasAdvancedCharts ? (
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "advanced"}
                onClick={() => selectActiveView("advanced")}
                className={joinClasses(
                  "inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-semibold transition-colors",
                  activeView === "advanced"
                    ? joinClasses(
                      palette.controlActiveBgClassName,
                      palette.controlActiveTextClassName,
                      "shadow-sm",
                    )
                    : joinClasses(
                      palette.controlInactiveTextClassName,
                      palette.controlInactiveHoverTextClassName,
                    ),
                )}
              >
                <LineChartIcon size={14} />
                <span>Advanced visuals</span>
              </button>
            ) : null}
            {graphViewEnabled ? (
              <button
                type="button"
                role="tab"
                aria-selected={activeView === "graph"}
                onClick={() => selectActiveView("graph")}
                className={joinClasses(
                  "inline-flex h-8 items-center gap-1.5 rounded px-2.5 text-xs font-semibold transition-colors",
                  activeView === "graph"
                    ? joinClasses(
                      palette.controlActiveBgClassName,
                      palette.controlActiveTextClassName,
                      "shadow-sm",
                    )
                    : joinClasses(
                      palette.controlInactiveTextClassName,
                      palette.controlInactiveHoverTextClassName,
                    ),
                )}
                data-testid="report-generation-advanced-visualization-graph-tab"
              >
                <Network size={14} />
                <span>Graph</span>
              </button>
            ) : null}
          </div>
          {activeView === "graph" ? (
            <div
              className={joinClasses(
                "inline-flex rounded-md border p-0.5",
                palette.controlBorderClassName,
                palette.controlBgClassName,
              )}
              aria-label="Graph camera controls"
            >
              <button
                type="button"
                onClick={toggleGraphAutoRotate}
                className={joinClasses(
                  "inline-flex h-8 w-8 items-center justify-center rounded transition-colors",
                  graphAutoRotate
                    ? joinClasses(
                      palette.controlActiveBgClassName,
                      palette.controlActiveTextClassName,
                      "shadow-sm",
                    )
                    : joinClasses(
                      palette.controlInactiveTextClassName,
                      palette.controlInactiveHoverTextClassName,
                    ),
                )}
                aria-label={graphAutoRotate ? "Pause graph rotation" : "Resume graph rotation"}
                aria-pressed={graphAutoRotate}
                title={graphAutoRotate ? "Pause rotation" : "Resume rotation"}
                data-testid="report-generation-advanced-visualization-graph-rotate-toggle"
              >
                {graphAutoRotate ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <button
                type="button"
                onClick={resetGraphView}
                className={joinClasses(
                  "inline-flex h-8 w-8 items-center justify-center rounded transition-colors",
                  palette.controlInactiveTextClassName,
                  palette.controlInactiveHoverTextClassName,
                )}
                aria-label="Reset graph view"
                title="Reset graph view"
                data-testid="report-generation-advanced-visualization-graph-reset"
              >
                <RotateCcw size={14} />
              </button>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className={joinClasses(
              "inline-flex h-8 w-8 items-center justify-center rounded-sm border transition-colors",
              palette.closeButtonBorderClassName,
              palette.closeButtonBgClassName,
              palette.closeButtonTextClassName,
              palette.closeButtonHoverBgClassName,
            )}
            aria-label="Close visualization"
          >
            <X size={15} />
          </button>
        </div>
      </div>
      <div
        className={joinClasses(
          "grid gap-0",
          isWorkspacePresentation ? "min-h-0 flex-1 items-stretch" : "items-start",
          graphDesktopLayout
            ? isWorkspacePresentation
              ? "grid-cols-[minmax(0,1fr)_300px]"
              : "grid-cols-[minmax(0,1fr)_260px]"
            : "grid-cols-1",
        )}
      >
        {activeView === "graph" ? (
          <div
            ref={mountRef}
            className={joinClasses("min-h-0 overflow-hidden", palette.stageBgClassName)}
            data-testid="report-generation-advanced-visualization-graph-stage"
            data-graph-scene-theme={surfaceTheme}
            data-graph-scene-background={graphSceneTheme.background ?? ""}
            style={{
              height: graphSceneHeight,
              maxHeight: graphSceneHeight,
            }}
          />
        ) : (
          <div
            className={joinClasses(
              isWorkspacePresentation
                ? "flex min-h-0 flex-col overflow-hidden p-4"
                : "min-h-[320px] overflow-hidden p-4",
              palette.stagePanelBgClassName,
            )}
            data-testid="report-generation-advanced-visualization-chart-stage"
            data-chart-scene-theme={surfaceTheme}
            data-chart-scene-background={chartSceneTheme.background ?? ""}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className={joinClasses("text-sm font-semibold", palette.headerTextPrimaryClassName)}>
                  {activeChartOption?.label ?? "Aggregate chart"}
                </p>
                <p className={joinClasses("mt-1 text-xs", palette.headerTextSecondaryClassName)}>
                  {activeView === "advanced"
                    ? "Three.js views for deeper visual exploration of the same selected rows"
                    : "Recharts views from selected visible columns and preview rows"}
                </p>
              </div>
              <div
                className={joinClasses(
                  "inline-flex rounded-md border p-0.5",
                  palette.chartSelectorBorderClassName,
                  palette.chartSelectorBgClassName,
                )}
                aria-label="Chart type selector"
              >
                {visibleChartTypes.map((chartType) => {
                  const Icon = CHART_TYPE_ICONS[chartType];
                  return (
                    <button
                      key={chartType}
                      type="button"
                      onClick={() => selectChartType(chartType)}
                      className={joinClasses(
                        "inline-flex h-8 items-center gap-1.5 rounded px-2 text-xs font-semibold transition-colors",
                        activeChartOption?.chartType === chartType
                          ? joinClasses(
                            palette.chartSelectorActiveBgClassName,
                            palette.chartSelectorActiveTextClassName,
                            "shadow-sm",
                          )
                          : joinClasses(
                            palette.chartSelectorInactiveTextClassName,
                            palette.chartSelectorInactiveHoverBgClassName,
                            palette.chartSelectorInactiveHoverTextClassName,
                          ),
                      )}
                      aria-label={`${CHART_TYPE_LABELS[chartType]} chart`}
                      title={`${CHART_TYPE_LABELS[chartType]} chart`}
                    >
                      <Icon size={14} />
                      <span>{CHART_TYPE_LABELS[chartType]}</span>
                    </button>
                  );
                })}
              </div>
              {activeChartOptionsForType.length > 1 ? (
                <select
                  value={activeChartOption?.id ?? ""}
                  onChange={(event) => setActiveChartOptionId(event.target.value)}
                  className={joinClasses(
                    "h-9 max-w-[260px] rounded-md border px-2 text-xs font-semibold outline-none transition-colors focus:border-[#2697A2]",
                    palette.chartSelectorBgClassName,
                    palette.chartSelectorBorderClassName,
                    palette.headerTextPrimaryClassName,
                  )}
                  data-testid="report-generation-advanced-visualization-chart-lens-select"
                  aria-label="Chart lens"
                >
                  {activeChartOptionsForType.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
            <div className="mt-4">
              {renderChartAxisLabelBadges(
                activeChartOption,
                palette,
                activeView === "advanced" ? "advanced" : "core",
              )}
            </div>
            <div
              className={joinClasses(
                "mt-4 rounded-md border px-3 py-4",
                isWorkspacePresentation ? "flex min-h-0 flex-col" : "",
                isWorkspacePresentation && activeView === "advanced" ? "flex-1" : "",
                palette.chartPanelBorderClassName,
                palette.chartPanelBgClassName,
              )}
              data-testid="report-generation-advanced-visualization-chart"
              data-chart-renderer={chartRenderer}
              data-chart-render-kind={chartRenderKind}
              data-chart-use-case-id={activeChartOption?.useCaseId ?? activeChartOption?.id ?? "none"}
              data-chart-renderer-state={activeChartRendererState}
              data-chart-source="preview-aggregate"
              data-chart-actual-data={String(activeChartPoints.length > 0)}
              data-chart-placeholder="false"
              data-chart-point-count={activeChartPoints.length}
              data-chart-bucket-signature={activeChartBucketSignature}
              data-chart-series-count={chartSeriesCount}
              data-chart-value-total={chartValueTotal}
              data-chart-camera-position={chartCameraPosition}
              data-chart-camera-target={chartCameraTarget}
              data-chart-scene-theme={surfaceTheme}
              data-chart-scene-background={chartSceneTheme.background ?? ""}
              data-chart-selected-field-signature={chartSnapshot?.summary.selectedFieldSignature ?? ""}
              data-chart-filter-signature={chartSnapshot?.summary.filterSignature ?? ""}
              data-chart-animation-variant={
                activeChartOption
                  ? resolveChartAnimationVariant(activeChartOption)
                  : "none"
              }
            >
              {activeChartOption && activeChartPoints.length > 0 ? (
                activeView === "advanced" && activeThreeChart ? (
                  <>
                    <div
                      key={activeChartProjectionSignature}
                      ref={chartMountRef}
                      className={joinClasses(
                        isWorkspacePresentation
                          ? "min-h-[500px] flex-1 overflow-hidden rounded-sm"
                          : "h-[420px] min-h-[360px] overflow-hidden rounded-sm",
                        palette.chartPanelBgClassName,
                      )}
                      data-testid="report-generation-advanced-visualization-chart-three-stage"
                      data-chart-renderer="threejs"
                      data-chart-render-kind={chartRenderKind}
                      data-chart-use-case-id={activeChartOption?.useCaseId ?? activeChartOption?.id ?? "none"}
                      data-chart-source="preview-aggregate"
                      data-chart-actual-data="true"
                      data-chart-placeholder="false"
                      data-chart-projection-signature={activeChartProjectionSignature}
                      data-chart-scene-theme={surfaceTheme}
                      data-chart-scene-background={chartSceneTheme.background ?? ""}
                    />
                    <div
                      className="mt-3 grid max-h-[76px] grid-cols-2 gap-2 overflow-y-auto pr-1 text-[11px] sm:grid-cols-3 lg:grid-cols-4"
                      data-testid="report-generation-advanced-visualization-chart-data-labels"
                    >
                      {activeThreeChartPoints.map(({ point, series }) => (
                        <div
                          key={point.id ?? point.key}
                          className={joinClasses(
                            "min-w-0 rounded-md border px-2 py-1.5",
                            palette.chartSelectorBgClassName,
                            palette.chartPanelBorderClassName,
                          )}
                          data-testid="report-generation-advanced-visualization-chart-data-label"
                          data-chart-series-id={series.id}
                          data-chart-series-label={series.label}
                        >
                          <p className={joinClasses("truncate font-semibold", palette.summaryValueTextClassName)}>
                            {series.label === activeChartOption?.metricLabel || activeThreeChart?.series.length === 1
                              ? point.label
                              : `${series.label} · ${point.label}`}
                          </p>
                          <p className={joinClasses("mt-0.5", palette.summaryTextClassName)}>
                            {formatChartValue(point.value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  renderRechartsFallbackChart(
                    activeChartOption,
                    activeChartPoints,
                    reducedMotion,
                    palette,
                    presentation,
                  )
                )
              ) : (
                <div
                  className={joinClasses(
                    isWorkspacePresentation
                      ? "flex h-[560px] min-h-[500px] items-center justify-center text-sm font-semibold"
                      : "flex h-[420px] items-center justify-center text-sm font-semibold",
                    palette.emptyStateTextClassName,
                  )}
                  style={isWorkspacePresentation ? { height: "min(60vh, 720px)" } : undefined}
                >
                  No compatible aggregate chart
                </div>
              )}
            </div>
            {chartSnapshot?.summary.warnings.length ? (
              <p className={joinClasses("mt-3 text-xs", palette.warningTextClassName)}>
                {chartSnapshot.summary.warnings[0]}
              </p>
            ) : null}
          </div>
        )}
        <div
          className={joinClasses(
            "overflow-y-auto p-4",
            graphDesktopLayout ? "border-l" : "border-t",
            palette.sideRailBorderClassName,
            palette.sideRailBgClassName,
          )}
          data-testid="report-generation-advanced-visualization-side-rail"
          style={isWorkspacePresentation && activeView !== "graph"
            ? undefined
            : {
              maxHeight: graphSceneHeight,
            }}
        >
          {renderSideRail()}
        </div>
      </div>
    </section>
  );
}
